import type { BookingSlot, TokenSession } from '@/features/storefront/api/storefront';
import type { StorefrontTeamMember } from '@/features/storefront/api/team';
import type {
  BookingHours,
  BookingService,
  BookingsSection,
} from '@/features/storefront/sections/schema';
import { memberHours } from '@/features/storefront/sections/team';

/**
 * The grid a day *would* have, before anything is booked.
 *
 * The server owns the real calendar - a slot list a browser reasoned its way
 * to is a list of times it merely hoped were free - so this is never what a
 * merchant's live shop offers a shopper. It exists for the two places where
 * there is no calendar on a server to ask about:
 *
 * - the **customiser**, where the section being designed has not been saved
 *   yet, and a booking widget that renders as an error message is one the
 *   merchant cannot see their own work in;
 * - a **theme's sample shop**, which belongs to nobody. Its cart adds up, its
 *   checkout files an order and its diary opens, all in the browser and all
 *   from `previewThemes/catalog/build.ts`; a calendar that answered "we
 *   couldn't load the times" would be the one dead control in it.
 *
 * One implementation for both, in a module of its own, because two restatements
 * of the same rule are two things to keep in step with the server.
 *
 * Times are built in the reader's own zone, which is what makes them print
 * correctly with no timezone at all: nobody's shop is behind these, so the
 * shop's ten o'clock is whatever ten o'clock the reader means.
 */
export function openSlots(
  section: BookingsSection,
  service: BookingService,
  day: string,
  /** One person's week, on a section that books by person; the section's otherwise. */
  week: BookingHours[] = section.hours,
): BookingSlot[] {
  const [year, month, date] = day.split('-').map(Number);
  const local = new Date(year, (month ?? 1) - 1, date ?? 1);
  const hours = week.find((row) => row.weekday === mondayFirst(local));
  if (!hours || hours.closed) return [];

  const [openHour, openMinute] = hours.opens.split(':').map(Number);
  const [closeHour, closeMinute] = hours.closes.split(':').map(Number);

  const opens = new Date(local);
  opens.setHours(openHour ?? 9, openMinute ?? 0, 0, 0);
  const closes = new Date(local);
  closes.setHours(closeHour ?? 17, closeMinute ?? 0, 0, 0);

  const slots: BookingSlot[] = [];
  const lengthMs = service.duration_minutes * 60_000;
  const stepMs = Math.max(1, section.slot_interval_minutes) * 60_000;

  for (let time = opens.getTime(); time + lengthMs <= closes.getTime(); time += stepMs) {
    slots.push({
      start_at: new Date(time).toISOString(),
      end_at: new Date(time + lengthMs).toISOString(),
      remaining: section.capacity_per_slot,
      available: true,
      team_member_ids: [],
    });
    // A runaway interval would hang the customiser rather than look wrong.
    if (slots.length > 200) break;
  }
  return slots;
}

/**
 * The same grid on a section that books by person: each person is one seat,
 * on their own hours.
 *
 * A time is offered when at least one of `people` works then and is not
 * `busy`; `team_member_ids` says who, which is what the picker narrows by and
 * what "any available" assigns from. One person in the list is that person's
 * own diary. The server's rule, restated for the two shops with no server -
 * see the note on `openSlots`.
 */
export function staffSlots(
  section: BookingsSection,
  service: BookingService,
  day: string,
  people: readonly Pick<StorefrontTeamMember, 'id' | 'hours'>[],
  busy: (memberId: string, startAt: string) => boolean = () => false,
): BookingSlot[] {
  const byStart = new Map<string, BookingSlot>();
  for (const person of people) {
    for (const slot of openSlots(section, service, day, memberHours(section, person))) {
      const entry =
        byStart.get(slot.start_at) ?? { ...slot, remaining: 0, available: false, team_member_ids: [] };
      if (!busy(person.id, slot.start_at)) entry.team_member_ids.push(person.id);
      entry.remaining = entry.team_member_ids.length;
      entry.available = entry.remaining > 0;
      byStart.set(slot.start_at, entry);
    }
  }
  return [...byStart.values()].sort((a, b) => a.start_at.localeCompare(b.start_at));
}

/** 0 for Monday, matching `BookingHours.weekday` and the server. */
export function mondayFirst(date: Date): number {
  return (date.getDay() + 6) % 7;
}

/**
 * `YYYY-MM-DD` for an instant, in the reader's own calendar.
 *
 * The reader's and not UTC, because that is the calendar the slots above were
 * built in - so a nine-in-the-evening appointment in a zone behind UTC belongs
 * to the day it was offered on rather than to tomorrow.
 */
export function isoDay(date: Date): string {
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
}

/**
 * Which session a token request is for. A person and a start, because with
 * "any available" two doctors' morning sessions can begin at the same time.
 */
export function sessionKey(session: Pick<TokenSession, 'team_member_id' | 'starts_at'>): string {
  return `${session.team_member_id}@${session.starts_at}`;
}
